<template>
  <div class="condicional-create-page">
    <div class="d-flex align-items-center justify-content-between mb-5">
      <div>
        <h3 class="mb-1">Nova Condicional de Mercadorias</h3>
        <span class="text-muted">
          Registre a saída temporária de mercadorias para o cliente.
        </span>
      </div>

      <button
        type="button"
        class="btn btn-light font-weight-bolder"
        @click="$router.push({ name: 'condicionalMercadorias' })"
      >
        Voltar
      </button>
    </div>

    <div class="card mb-5">
      <div class="card-body">
        <b-row>
          <b-col md="3">
            <b-form-group label="Cliente" label-for="cliente">
              <b-form-input
                v-if="form.responsabilidade_vendedor"
                id="cliente"
                value="Responsabilidade do vendedor"
                readonly
              />
              <VueSelect
                v-else
                id="cliente"
                v-model="form.cliente"
                :options="clientes"
                :filterable="false"
                label="nome"
                class="busca-cliente"
                placeholder="Informe o nome do cliente"
                @search="buscarClientes"
              >
                <template #option="option">
                  <div>
                    <strong>{{ option.nome }}</strong>
                    <div class="text-muted small">
                      {{ option.cpf_cnpj || option.cnpj_cpf || "-" }}
                    </div>
                  </div>
                </template>
                <template #selected-option="option">
                  {{ option.nome }}
                </template>
                <template #no-options>Informe o nome do cliente</template>
              </VueSelect>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group
              label="Responsabilidade do vendedor"
              label-for="responsabilidade_vendedor"
            >
              <b-form-checkbox
                id="responsabilidade_vendedor"
                v-model="form.responsabilidade_vendedor"
                class="condicional-responsabilidade-flag"
                switch
                @change="alterarResponsabilidadeVendedor"
              >
                Sim
              </b-form-checkbox>
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group label="Vendedor" label-for="vendedor">
              <VueSelect
                id="vendedor"
                v-model="form.vendedor"
                :options="vendedores"
                :filterable="false"
                label="nome"
                placeholder="Informe o vendedor"
                @search="buscarVendedores"
              >
                <template #no-options>Informe o nome do vendedor</template>
              </VueSelect>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Data de saída" label-for="data_saida">
              <b-form-input
                id="data_saida"
                v-model="form.data_saida"
                type="date"
              />
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group
              label="Limite de retorno"
              label-for="data_limite_retorno"
            >
              <b-form-input
                id="data_limite_retorno"
                v-model="form.data_limite_retorno"
                type="date"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Observação" label-for="observacao" class="mb-0">
          <b-form-textarea
            id="observacao"
            v-model="form.observacao"
            rows="2"
            max-rows="4"
          />
        </b-form-group>
      </div>
    </div>

    <div class="card mb-5">
      <div class="card-body">
        <b-row align-v="end">
          <b-col md="5">
            <b-form-group label="Produto" label-for="produto">
              <VueSelect
                id="produto"
                ref="produtoSelect"
                v-model="produtoSelecionado"
                :options="produtos"
                :filterable="false"
                label="descricao"
                placeholder="Busque por nome, código ou código de barras"
                @search="buscarProdutos"
                @input="selecionarProduto"
              >
                <template #option="option">
                  <div>
                    <strong>{{ option.descricao }}</strong>
                    <div class="text-muted small">
                      Código: {{ option.codigo || "-" }} · Estoque:
                      {{ formatQuantidade(estoqueDisponivel(option)) }}
                      <span v-if="combinacaoLabel(option)" class="ml-2">
                        · {{ combinacaoLabel(option) }}
                      </span>
                    </div>
                  </div>
                </template>
                <template #selected-option="option">
                  {{ option.descricao }}
                </template>
                <template #no-options>Informe ao menos 3 caracteres</template>
              </VueSelect>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Estoque disponível" label-for="estoque">
              <b-form-input
                id="estoque"
                :value="formatQuantidade(estoqueProdutoSelecionado)"
                readonly
              />
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Quantidade" label-for="quantidade">
              <b-form-input
                id="quantidade"
                v-model.number="item.quantidade"
                type="number"
                min="0.0001"
                step="0.0001"
              />
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Valor unitário" label-for="valor_unitario">
              <b-form-input
                id="valor_unitario"
                v-model.number="item.valor_unitario"
                type="number"
                min="0"
                step="0.01"
              />
            </b-form-group>
          </b-col>

          <b-col md="1">
            <button
              type="button"
              class="btn btn-primary btn-block"
              v-b-tooltip.hover.bottom="'Adicionar item'"
              :disabled="carregandoEstoqueProduto"
              @click="adicionarItem"
            >
              +
            </button>
          </b-col>
        </b-row>

        <div class="table-responsive mt-4">
          <table class="table table-sm table-striped">
            <thead>
              <tr>
                <th>Produto</th>
                <th class="text-right">Estoque</th>
                <th class="text-right">Quantidade</th>
                <th class="text-right">Valor unitário</th>
                <th class="text-right">Total</th>
                <th class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="form.itens.length === 0">
                <td colspan="6" class="text-center text-muted py-6">
                  Nenhum produto incluído.
                </td>
              </tr>
              <tr v-for="(itemLinha, index) in form.itens" :key="itemLinha.uid">
                <td>
                  <strong>{{ itemLinha.descricao }}</strong>
                  <div class="text-muted small">
                    Código: {{ itemLinha.codigo || "-" }}
                    <span v-if="combinacaoLabel(itemLinha)" class="ml-2">
                      · {{ combinacaoLabel(itemLinha) }}
                    </span>
                  </div>
                </td>
                <td class="text-right">
                  {{ formatQuantidade(itemLinha.estoque_disponivel) }}
                </td>
                <td class="text-right">
                  <b-form-input
                    v-model.number="itemLinha.quantidade"
                    class="text-right condicional-number-input"
                    type="number"
                    min="0.0001"
                    step="0.0001"
                    @change="validarQuantidadeLinha(itemLinha)"
                  />
                </td>
                <td class="text-right">
                  <b-form-input
                    v-model.number="itemLinha.valor_unitario"
                    class="text-right condicional-number-input"
                    type="number"
                    min="0"
                    step="0.01"
                  />
                </td>
                <td class="text-right">
                  R$ {{ formatValor(totalItem(itemLinha)) }}
                </td>
                <td class="text-center">
                  <button
                    type="button"
                    class="btn btn-sm btn-danger"
                    @click="removerItem(index)"
                  >
                    Remover
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="2">Total</th>
                <th class="text-right">
                  {{ formatQuantidade(totalQuantidade) }}
                </th>
                <th></th>
                <th class="text-right">R$ {{ formatValor(totalGeral) }}</th>
                <th></th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end">
      <button
        type="button"
        class="btn btn-light-primary font-weight-bolder mr-3"
        :disabled="salvando"
        @click="salvar(false)"
      >
        Salvar rascunho
      </button>
      <button
        type="button"
        class="btn btn-primary font-weight-bolder"
        :disabled="salvando"
        @click="confirmarSalvarSaida"
      >
        Salvar e confirmar saída
      </button>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import moment from "moment";
import swal from "sweetalert";
import VueSelect from "vue-select";
import { mapState } from "vuex";
import { SistemaMixin } from "@/modules/Application";
import CondicionalMercadoriaData from "../CondicionalMercadoriaData";

export default {
  name: "condicionalMercadoriasNova",
  mixins: [SistemaMixin],
  components: {
    VueSelect,
  },
  data() {
    return {
      salvando: false,
      clientes: [],
      vendedores: [],
      produtos: [],
      produtoSelecionado: null,
      carregandoEstoqueProduto: false,
      form: {
        cliente: null,
        vendedor: null,
        responsabilidade_vendedor: false,
        data_saida: moment().format("YYYY-MM-DD"),
        data_limite_retorno: moment().add(7, "days").format("YYYY-MM-DD"),
        observacao: "",
        itens: [],
      },
      item: {
        quantidade: 1,
        valor_unitario: 0,
      },
    };
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.auth.user,
    }),
    estoqueProdutoSelecionado() {
      return this.estoqueDisponivel(this.produtoSelecionado);
    },
    totalQuantidade() {
      return this.form.itens.reduce(
        (total, item) => total + this.toFloat(item.quantidade),
        0
      );
    },
    totalGeral() {
      return this.form.itens.reduce(
        (total, item) => total + this.totalItem(item),
        0
      );
    },
  },
  mounted() {
    if (this.currentUser?.vendedor) {
      this.form.vendedor = this.currentUser.vendedor;
    }
  },
  methods: {
    alterarResponsabilidadeVendedor() {
      if (this.form.responsabilidade_vendedor) {
        this.form.cliente = null;
      }
    },
    buscarClientes(search, loading) {
      if (!search || search.length < 2) return;

      loading(true);
      this.buscarClientesDebounced(search, loading, this);
    },
    buscarClientesDebounced: debounce(async (search, loading, vm) => {
      vm.clientes = await vm.getClientes(search);
      loading(false);
    }, 350),
    buscarVendedores(search, loading) {
      if (!search || search.length < 2) return;

      loading(true);
      this.buscarVendedoresDebounced(search, loading, this);
    },
    buscarVendedoresDebounced: debounce(async (search, loading, vm) => {
      vm.vendedores = await vm.getVendedorByNome(search);
      loading(false);
    }, 350),
    buscarProdutos(search, loading) {
      if (!search || search.length < 3) return;

      loading(true);
      this.buscarProdutosDebounced(search, loading, this);
    },
    buscarProdutosDebounced: debounce(async (search, loading, vm) => {
      try {
        const { data } = await vm.$http.post("/produtos/filter-item", {
          filter: search,
          typeFilter: 1,
          nfce: true,
          apresentar_variacoes: true,
          considerar_somente_qtd: false,
        });

        vm.produtos = data || [];
        await vm.adicionarProdutoPorCodigoBarras(search);
      } catch (error) {
        vm.produtos = [];
      } finally {
        loading(false);
      }
    }, 350),
    async selecionarProduto(produto, adicionarAutomaticamente = false) {
      if (!produto) return;

      this.carregandoEstoqueProduto = true;

      try {
        const estoque = await CondicionalMercadoriaData.estoqueDisponivel(
          this,
          {
            id_produto: produto.id,
            id_produto_lote: produto.lote_baixa?.id_produto_lote || null,
            combinacao: produto.combinacao || null,
          }
        );

        this.produtoSelecionado = {
          ...produto,
          estoque_disponivel: estoque.estoque_disponivel,
        };
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ||
            error?.message ||
            "Erro ao consultar estoque disponível."
        );
        this.produtoSelecionado = null;
      } finally {
        this.carregandoEstoqueProduto = false;
      }

      if (!this.produtoSelecionado) return;

      this.item.valor_unitario = this.toFloat(
        this.produtoSelecionado.preco_lista_cliente ||
          this.produtoSelecionado.preco ||
          0
      );
      this.item.quantidade = 1;

      if (adicionarAutomaticamente) {
        this.adicionarItem();
      }
    },
    async adicionarProdutoPorCodigoBarras(search) {
      const codigoBarras = this.normalizarCodigoBarras(search);

      if (
        !codigoBarras ||
        codigoBarras.length < 6 ||
        this.produtos.length !== 1
      ) {
        return;
      }

      const produto = this.produtos[0];
      const codigoProduto = this.normalizarCodigoBarras(produto.cod_barras);
      const codigoProdutoBalanca =
        codigoBarras.length === 13 && codigoBarras[0] === "2"
          ? codigoBarras.slice(0, 7)
          : null;

      if (
        codigoProduto !== codigoBarras &&
        codigoProduto !== codigoProdutoBalanca
      ) {
        return;
      }

      await this.selecionarProduto(produto, true);
    },
    adicionarItem() {
      if (!this.produtoSelecionado) {
        this.$toast.warning("Selecione um produto.");
        return;
      }

      const quantidade = this.toFloat(this.item.quantidade);
      const valorUnitario = this.toFloat(this.item.valor_unitario);
      const estoque = this.estoqueDisponivel(this.produtoSelecionado);
      const itemExistente = this.form.itens.find(
        (itemLinha) =>
          this.chaveEstoqueItem(itemLinha) ===
          this.chaveEstoqueProduto(this.produtoSelecionado)
      );
      const quantidadeFinal = itemExistente
        ? this.toFloat(itemExistente.quantidade) + quantidade
        : quantidade;

      if (
        this.produtoSelecionado.variavel &&
        !this.produtoSelecionado.combinacao
      ) {
        this.$toast.warning("Selecione uma variação para este produto.");
        return;
      }

      if (quantidade <= 0) {
        this.$toast.warning("Informe uma quantidade maior que zero.");
        return;
      }

      if (valorUnitario < 0) {
        this.$toast.warning("Informe um valor unitário válido.");
        return;
      }

      if (estoque !== null && quantidadeFinal > estoque) {
        this.$toast.warning("Quantidade informada é superior ao estoque.");
        return;
      }

      if (itemExistente) {
        itemExistente.quantidade = quantidadeFinal;
        itemExistente.estoque_disponivel = estoque;
        this.limparProdutoSelecionado();
        return;
      }

      this.form.itens.push({
        uid: `${this.produtoSelecionado.id}-${Date.now()}-${Math.random()}`,
        id_produto: this.produtoSelecionado.id,
        id_produto_lote:
          this.produtoSelecionado.lote_baixa?.id_produto_lote || null,
        combinacao: this.produtoSelecionado.combinacao || null,
        variavel: Boolean(this.produtoSelecionado.variavel),
        descricao: this.produtoSelecionado.descricao,
        codigo: this.produtoSelecionado.codigo,
        estoque_disponivel: estoque,
        quantidade,
        valor_unitario: valorUnitario,
      });

      this.limparProdutoSelecionado();
    },
    limparProdutoSelecionado() {
      this.produtoSelecionado = null;
      this.produtos = [];
      if (this.$refs.produtoSelect) {
        this.$refs.produtoSelect.search = "";
      }
      this.item = {
        quantidade: 1,
        valor_unitario: 0,
      };
      this.$nextTick(() => {
        this.$refs.produtoSelect?.$el?.querySelector("input")?.focus();
      });
    },
    removerItem(index) {
      this.form.itens.splice(index, 1);
    },
    validarQuantidadeLinha(item) {
      const quantidade = this.toFloat(item.quantidade);

      if (quantidade <= 0) {
        item.quantidade = 1;
        this.$toast.warning("Quantidade deve ser maior que zero.");
        return;
      }

      if (
        item.estoque_disponivel !== null &&
        quantidade > this.toFloat(item.estoque_disponivel)
      ) {
        item.quantidade = this.toFloat(item.estoque_disponivel);
        this.$toast.warning("Quantidade ajustada ao estoque disponível.");
      }
    },
    validarFormulario() {
      if (
        !this.form.responsabilidade_vendedor &&
        !this.form.cliente?.id_pessoa
      ) {
        this.$toast.warning("Informe o cliente.");
        return false;
      }

      if (
        this.form.responsabilidade_vendedor &&
        !this.form.vendedor?.id_vendedor
      ) {
        this.$toast.warning("Informe o vendedor responsável.");
        return false;
      }

      if (!this.form.data_limite_retorno) {
        this.$toast.warning("Informe a data limite de retorno.");
        return false;
      }

      if (
        this.form.data_saida &&
        moment(this.form.data_limite_retorno).isBefore(
          moment(this.form.data_saida),
          "day"
        )
      ) {
        this.$toast.warning("Data limite deve ser igual ou posterior à saída.");
        return false;
      }

      if (this.form.itens.length === 0) {
        this.$toast.warning("Informe ao menos um produto.");
        return false;
      }

      const itemInvalido = this.form.itens.find(
        (item) =>
          this.toFloat(item.quantidade) <= 0 ||
          this.toFloat(item.valor_unitario) < 0
      );

      if (itemInvalido) {
        this.$toast.warning("Revise quantidades e valores dos itens.");
        return false;
      }

      return true;
    },
    montarPayload(confirmarSaida = false) {
      return {
        responsabilidade_vendedor: this.form.responsabilidade_vendedor,
        cliente: this.form.responsabilidade_vendedor ? null : this.form.cliente,
        id_pessoa: this.form.responsabilidade_vendedor
          ? null
          : this.form.cliente?.id_pessoa,
        vendedor: this.form.vendedor,
        id_vendedor: this.form.vendedor?.id_vendedor || null,
        data_saida: this.form.data_saida || null,
        data_limite_retorno: this.form.data_limite_retorno,
        observacao: this.form.observacao || null,
        confirmar_saida: confirmarSaida,
        itens: this.form.itens.map((item) => ({
          id_produto: item.id_produto,
          id_produto_lote: item.id_produto_lote,
          combinacao: item.combinacao,
          quantidade: this.toFloat(item.quantidade),
          valor_unitario: this.toFloat(item.valor_unitario),
          descricao_produto_snapshot: item.descricao,
          codigo_produto_snapshot: item.codigo,
        })),
      };
    },
    async confirmarSalvarSaida() {
      swal({
        title: "Salvar e confirmar saída?",
        text: "O estoque ficará reservado para esta condicional.",
        icon: "info",
        buttons: {
          cancel: { text: "Cancelar", value: false, visible: true },
          confirm: { text: "Confirmar", value: true, visible: true },
        },
      }).then((confirmou) => {
        if (confirmou) {
          this.salvar(true);
        }
      });
    },
    async salvar(confirmarSaida) {
      if (!this.validarFormulario()) return;

      this.salvando = true;

      try {
        if (!(await this.validarEstoquesAntesSalvar())) {
          return;
        }

        await CondicionalMercadoriaData.create(
          this,
          this.montarPayload(confirmarSaida)
        );

        if (confirmarSaida) {
          this.$toast.success("Saída confirmada e estoque reservado.");
        } else {
          this.$toast.success("Condicional criada com sucesso.");
        }

        this.$router.push({ name: "condicionalMercadorias" });
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ||
            error?.message ||
            "Erro ao salvar condicional."
        );
      } finally {
        this.salvando = false;
      }
    },
    async validarEstoquesAntesSalvar() {
      const grupos = this.form.itens.reduce((acc, item) => {
        const chave = this.chaveEstoqueItem(item);

        if (!acc[chave]) {
          acc[chave] = {
            itemReferencia: item,
            quantidade: 0,
            itens: [],
          };
        }

        acc[chave].quantidade += this.toFloat(item.quantidade);
        acc[chave].itens.push(item);

        return acc;
      }, {});

      for (const grupo of Object.values(grupos)) {
        const estoque = await CondicionalMercadoriaData.estoqueDisponivel(
          this,
          {
            id_produto: grupo.itemReferencia.id_produto,
            id_produto_lote: grupo.itemReferencia.id_produto_lote || null,
            combinacao: grupo.itemReferencia.combinacao || null,
          }
        );
        const estoqueDisponivel = this.toFloat(estoque.estoque_disponivel);

        grupo.itens.forEach((item) => {
          item.estoque_disponivel = estoqueDisponivel;
        });

        if (grupo.quantidade > estoqueDisponivel + 0.0001) {
          this.$toast.error(
            `Estoque disponível insuficiente para ${
              grupo.itemReferencia.descricao
            }. Disponível: ${this.formatQuantidade(estoqueDisponivel)}.`
          );

          return false;
        }
      }

      return true;
    },
    chaveEstoqueItem(item) {
      return JSON.stringify({
        id_produto: item.id_produto,
        id_produto_lote: item.id_produto_lote || null,
        combinacao: item.combinacao || null,
      });
    },
    chaveEstoqueProduto(produto) {
      return JSON.stringify({
        id_produto: produto.id,
        id_produto_lote: produto.lote_baixa?.id_produto_lote || null,
        combinacao: produto.combinacao || null,
      });
    },
    normalizarCodigoBarras(codigo) {
      return String(codigo || "").trim();
    },
    estoqueDisponivel(produto) {
      if (!produto) return null;

      const estoque =
        produto.estoque_disponivel ??
        produto.quantidade_original_combinacao ??
        produto.combinacao?.quantidade ??
        produto.quantidade_original ??
        produto.quantidade_original_produto ??
        produto.estoque ??
        produto.saldo_estoque ??
        produto.quantidade;

      if (estoque === undefined || estoque === null || estoque === "") {
        return null;
      }

      return this.toFloat(estoque);
    },
    combinacaoLabel(item) {
      const combinacao = item?.combinacao;

      if (!combinacao) return "";

      return Object.keys(combinacao)
        .filter((key) => key !== "quantidade")
        .map((key) => {
          const value = combinacao[key];
          const label = value && typeof value === "object" ? value.nome : value;
          return label ? `${key}: ${label}` : "";
        })
        .filter(Boolean)
        .join(" / ");
    },
    totalItem(item) {
      return this.toFloat(item.quantidade) * this.toFloat(item.valor_unitario);
    },
    toFloat(value) {
      if (value === undefined || value === null || value === "") return 0;
      if (typeof value === "string") {
        if (value.includes(",")) {
          return Number(value.replace(/\./g, "").replace(",", "."));
        }

        return Number(value);
      }

      return Number(value);
    },
    formatQuantidade(value) {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return "-";
      }

      return this.toFloat(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    },
    formatValor(value) {
      return this.toFloat(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.condicional-create-page .card {
  border-radius: 6px;
}

.condicional-number-input {
  min-width: 110px;
}

.condicional-responsabilidade-flag {
  align-items: center;
  display: flex;
  min-height: 43px;
}
</style>
